
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CartItem, MovementType } from '../types';
import { fetchVariantBySku, saveMovementBatch, quickCreateVariant } from '../services/db';
import { playScanBeep, playErrorBeep } from '../services/sound';
import { Scanner } from '../components/Scanner';
import { IconCamera, IconArrowDown, IconArrowUp, IconTrash, IconQrCode } from '../components/Icons';
import { useI18n } from '../i18n';

export const Movements: React.FC = () => {
  const [mode, setMode] = useState<MovementType>('IN');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [manualSku, setManualSku] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
  const { t } = useI18n();

  // Debounce Refs
  const lastScannedRef = useRef<{ sku: string; time: number } | null>(null);
  const DEBOUNCE_MS = 2000;

  // Process a scanned SKU
  const handleScan = useCallback(async (sku: string) => {
    const now = Date.now();
    
    // Check debounce
    if (
      lastScannedRef.current &&
      lastScannedRef.current.sku === sku &&
      now - lastScannedRef.current.time < DEBOUNCE_MS
    ) {
      console.log('Debounced:', sku);
      return;
    }

    lastScannedRef.current = { sku, time: now };

    try {
      // 1. Try to fetch existing
      let variant = await fetchVariantBySku(sku);
      let isNew = false;
      
      // 2. If not found, Auto-Create (New Feature)
      if (!variant) {
        try {
          // Notify user creation is happening
          setToast({ msg: t('movements.toast.newItem', { sku }), type: 'success' });
          variant = await quickCreateVariant(sku);
          isNew = true;
        } catch (createErr) {
          playErrorBeep();
          setToast({ msg: t('movements.toast.registerError', { sku }), type: 'error' });
          return;
        }
      }

      playScanBeep();
      
      // Add to cart
      setCart(prev => {
        const existingIdx = prev.findIndex(item => item.variant.sku === sku);
        if (existingIdx >= 0) {
          const newCart = [...prev];
          newCart[existingIdx].quantity += 1;
          return newCart;
        } else {
          return [{ variant: variant!, quantity: 1 }, ...prev];
        }
      });
      
      const actionMsg = isNew
        ? t('movements.toast.newItem', { sku: variant!.sku })
        : t('movements.toast.scanned', { sku: variant!.sku });
      setToast({ msg: actionMsg, type: 'success' });

    } catch (err) {
      playErrorBeep();
      console.error(err);
      setToast({ msg: t('movements.toast.scanError'), type: 'error' });
    }
  }, [t]);

  // Keyboard listener for handheld scanners (they usually type SKU + Enter)
  useEffect(() => {
    let buffer = '';
    let timeout: ReturnType<typeof setTimeout>;

    const handleKeyDown = (e: KeyboardEvent) => {
      // If user is typing in the manual input, ignore global listener
      if ((e.target as HTMLElement).tagName === 'INPUT') return;

      if (e.key === 'Enter') {
        if (buffer.length > 3) {
          handleScan(buffer);
        }
        buffer = '';
      } else if (e.key.length === 1) {
        buffer += e.key;
        // Clear buffer if typing too slow (handheld scanners are fast)
        clearTimeout(timeout);
        timeout = setTimeout(() => { buffer = ''; }, 100);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleScan]);

  // Remove item from cart
  const removeFromCart = (sku: string) => {
    setCart(prev => prev.filter(i => i.variant.sku !== sku));
  };

  // Adjust quantity manually
  const updateQuantity = (sku: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.variant.sku === sku) {
        return { ...item, quantity: Math.max(1, item.quantity + delta) };
      }
      return item;
    }));
  };

  // Submit Batch
  const handleConfirm = async () => {
    if (cart.length === 0) return;
    setLoading(true);
    
    try {
      const items = cart.map(c => ({ variant_id: c.variant.id, quantity: c.quantity }));
      await saveMovementBatch(mode, items, `Manual ${mode} via App`);

      setCart([]);
      setToast({ msg: t('movements.toast.saved'), type: 'success' });
      setManualSku('');
    } catch (e) {
      setToast({ msg: t('movements.toast.saveError'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Clear toast after 3s
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // Handle manual input submit
  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualSku) {
      handleScan(manualSku);
      setManualSku('');
    }
  };

  return (
    <div className="space-y-4 pb-20 text-primary">
      {/* Header / Mode Switcher */}
      <div className="bg-surface p-4 rounded-xl shadow-sm border border-subtle sticky top-0 z-10">
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode('IN')}
            className={`flex-1 py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all border ${
              mode === 'IN'
                ? 'bg-success-soft text-success border-success'
                : 'bg-surface-strong text-muted border-subtle hover:text-primary'
            }`}
          >
            <IconArrowDown className="w-4 h-4" /> {t('movements.entry').toUpperCase()}
          </button>
          <button
            onClick={() => setMode('OUT')}
            className={`flex-1 py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all border ${
              mode === 'OUT'
                ? 'bg-danger-soft text-danger border-danger'
                : 'bg-surface-strong text-muted border-subtle hover:text-primary'
            }`}
          >
            <IconArrowUp className="w-4 h-4" /> {t('movements.exit').toUpperCase()}
          </button>
        </div>

        {/* Action Bar */}
        <div className="flex gap-2">
          <form onSubmit={handleManualSubmit} className="flex-1 relative">
             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconQrCode className="h-5 w-5 text-muted" />
             </div>
             <input
               type="text"
               value={manualSku}
               onChange={(e) => setManualSku(e.target.value.toUpperCase())}
               placeholder={t('movements.manual.placeholder')}
               className="w-full pl-10 pr-4 py-3 bg-surface-strong border border-subtle rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-focus)] focus:outline-none"
             />
          </form>
          <button
            onClick={() => setIsScannerOpen(true)}
            className="bg-accent text-white p-3 rounded-lg shadow-md active:scale-95 transition-transform"
            aria-label="Open Camera"
          >
            <IconCamera className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Cart List */}
      <div className="space-y-3">
        {cart.length === 0 ? (
          <div className="text-center py-12 text-muted bg-surface rounded-xl border border-dashed border-subtle">
            <IconQrCode className="w-12 h-12 mx-auto mb-2 opacity-20" />
            <p>{t('movements.ready')}</p>
          </div>
        ) : (
          cart.map((item) => (
            <div key={item.variant.sku} className="bg-surface p-4 rounded-xl shadow-sm border border-subtle flex items-center justify-between animate-in fade-in slide-in-from-bottom-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                   <h4 className="font-bold text-primary truncate">{item.variant.sku}</h4>
                   <span className="text-[10px] px-2 py-0.5 bg-surface-strong text-muted rounded-full font-mono">{t('movements.cart.badge', { color: item.variant.color, size: item.variant.size })}</span>
                </div>
                <p className="text-xs text-muted truncate">{item.variant.productName}</p>
              </div>

              <div className="flex items-center gap-3 pl-4">
                <div className="flex items-center bg-surface-strong rounded-lg border border-subtle">
                  <button onClick={() => updateQuantity(item.variant.sku, -1)} className="w-8 h-8 flex items-center justify-center text-primary hover:bg-surface rounded-l-lg">-</button>
                  <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.variant.sku, 1)} className="w-8 h-8 flex items-center justify-center text-primary hover:bg-surface rounded-r-lg">+</button>
                </div>
                <button onClick={() => removeFromCart(item.variant.sku)} className="text-muted hover:text-danger p-1">
                  <IconTrash className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Floating Confirm Button */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+5rem)] bg-surface border-t border-subtle z-60 md:static md:bg-transparent md:border-0 md:p-0 md:pb-0 md:pt-0 md:px-0">
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`w-full py-4 rounded-xl font-bold text-white shadow-lg flex items-center justify-center gap-2 ${
              mode === 'IN' ? 'bg-success' : 'bg-danger'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading
              ? t('movements.saving')
              : t('movements.confirm', { mode: mode === 'IN' ? t('movements.entry').toUpperCase() : t('movements.exit').toUpperCase(), count: cart.reduce((a, b) => a + b.quantity, 0) })}
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-sm font-medium shadow-xl z-50 animate-in fade-in slide-in-from-top-2 ${
          toast.type === 'success' ? 'bg-accent text-white' : 'bg-danger text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Scanner Overlay */}
      <Scanner 
        isActive={isScannerOpen} 
        onScan={(sku) => {
          handleScan(sku);
          // Optional: close scanner after one scan? usually no for bulk
          // setIsScannerOpen(false); 
        }} 
        onClose={() => setIsScannerOpen(false)} 
      />
    </div>
  );
};
